import { useCallback } from 'react';
import { gqlRequestMediaType, gqlMediaTypeCodeEnum as MEDIA_TYPE } from '@minecraft.graphql-types';
import { appConfig } from '@minecraft.app-config';
import { useTranslation, TranslateFnOptions } from '@blocs.i18n';
import { MediaTypeCodeEnum } from '@minecraft.graphql-operations';

export type MediaTextKey =
  | 'add'
  | 'addClip'
  | 'addThis'
  | 'confirmDelete'
  | 'confirmDelete'
  | 'count'
  | 'delete'
  | 'delete'
  | 'deleteAll'
  | 'deleteAll'
  | 'deleteAllWarning'
  | 'deleteAllWarning'
  | 'deleteWarning'
  | 'deleteWarning'
  | 'edit'
  | 'editFailure'
  | 'editSuccess'
  | 'noFiles'
  | 'noFiles'
  | 'noneOnProfile'
  | 'noneOnProfile'
  | 'play'
  | 'playlistLabel'
  | 'pluralTitle'
  | 'prefixCountInParenthesis'
  | 'primary'
  | 'rearrangeSuccess'
  | 'rearrangeTitle'
  | 'requested'
  | 'setAsPrimary'
  | 'setAsPrimaryShort'
  | 'removeAsPrimary'
  | 'suffixCountInParenthesis'
  | 'title'
  | 'upload'
  | 'uploadSuccess'
  | 'noMediaCurrently';

type MediaHelperKey = 'audio' | 'document' | 'video' | 'photo' | 'pdf';

export type MediaTextFn = (
  mediaType: MEDIA_TYPE | gqlRequestMediaType | MediaTypeCodeEnum,
  type: MediaTextKey,
  opts?: TranslateFnOptions
) => string;

const media: { [K in MediaHelperKey]: { [MK in MediaTextKey]: string } } = {
  audio: {
    add: 'common:mediaHelper.audio.add',
    addClip: 'common:mediaHelper.audio.addClip',
    addThis: 'common:mediaHelper.audio.add',
    confirmDelete: 'common:mediaHelper.audio.confirmDelete',
    count: 'common:mediaHelper.audio.count',
    delete: 'common:mediaHelper.audio.delete',
    deleteAll: 'common:mediaHelper.audio.deleteAll',
    deleteAllWarning: 'common:mediaHelper.audio.deleteAllWarning',
    deleteWarning: 'common:mediaHelper.audio.deleteWarning',
    edit: 'common:mediaHelper.audio.edit',
    editFailure: 'common:mediaHelper.audio.editFailure',
    editSuccess: 'common:mediaHelper.audio.editSuccess',
    noFiles: 'common:mediaHelper.audio.noFiles',
    noneOnProfile: 'common:mediaHelper.audio.noneShownOnProfile',
    play: 'common:mediaHelper.audio.play',
    playlistLabel: 'common:mediaType.audioPlaylist',
    pluralTitle: 'common:mediaType.audio_other',
    prefixCountInParenthesis: 'common:mediaHelper.audio.prefixCountInParenthesis',
    primary: 'common:profile.primaryAudio',
    rearrangeSuccess: 'common:mediaHelper.audio.rearrangeSuccess',
    rearrangeTitle: 'common:mediaHelper.audio.rearrangeTitle',
    requested: 'common:mediaHelper.audio.requested',
    setAsPrimary: 'common:profile.button.setAudioReel',
    setAsPrimaryShort: 'common:profile.button.setAudioReel',
    suffixCountInParenthesis: 'common:mediaHelper.audio.suffixCountInParenthesis',
    title: 'common:mediaType.audio',
    upload: 'common:mediaHelper.audio.upload',
    uploadSuccess: 'common:mediaHelper.audio.uploadSuccess',
    removeAsPrimary: 'common:mediaHelper.audio.removeAsPrimary',
    noMediaCurrently: 'common:mediaHelper.audio.noMediaCurrentlyOnProfile',
  },
  document: {
    add: 'common:mediaHelper.document.add',
    addClip: 'common:mediaHelper.document.addClip',
    addThis: 'common:mediaHelper.document.add',
    count: 'common:mediaHelper.document.count',
    deleteAll: 'common:mediaHelper.document.deleteAll',
    deleteAllWarning: 'common:mediaHelper.document.deleteAllWarning',
    edit: 'common:mediaHelper.document.edit',
    editFailure: 'common:mediaHelper.document.editFailure',
    editSuccess: 'common:mediaHelper.document.editSuccess',
    noFiles: 'common:mediaHelper.document.noFiles',
    play: 'common:mediaHelper.document.play',
    pluralTitle: 'common:mediaType.document_other',
    prefixCountInParenthesis: 'common:mediaHelper.document.prefixCountInParenthesis',
    requested: 'common:mediaHelper.document.requested',
    rearrangeSuccess: 'common:mediaHelper.document.rearrangeSuccess',
    rearrangeTitle: 'common:mediaHelper.document.rearrangeTitle',
    suffixCountInParenthesis: 'common:mediaHelper.document.suffixCountInParenthesis',
    title: 'common:mediaType.document',
    upload: 'common:mediaHelper.document.upload',
    uploadSuccess: 'common:mediaHelper.document.uploadSuccess',
    // Deleting Documents is not allowed
    delete: '',
    deleteWarning: '',
    confirmDelete: '',
    noneOnProfile: '',
    setAsPrimary: '',
    setAsPrimaryShort: '',
    // There is no "primary" document
    primary: '',
    removeAsPrimary: '',
    noMediaCurrently: '',
    playlistLabel: '',
  },
  video: {
    add: 'common:mediaHelper.video.add',
    addClip: 'common:mediaHelper.video.addClip',
    addThis: 'common:mediaHelper.video.add',
    confirmDelete: 'common:mediaHelper.video.confirmDelete',
    count: 'common:mediaHelper.video.count',
    delete: 'common:mediaHelper.video.delete',
    deleteAll: 'common:mediaHelper.video.deleteAll',
    deleteAllWarning: 'common:mediaHelper.video.deleteAllWarning',
    deleteWarning: 'common:mediaHelper.video.deleteWarning',
    edit: 'common:mediaHelper.video.edit',
    editFailure: 'common:mediaHelper.video.editFailure',
    editSuccess: 'common:mediaHelper.video.editSuccess',
    noFiles: 'common:mediaHelper.video.noFiles',
    noneOnProfile: 'common:mediaHelper.video.noneShownOnProfile',
    play: 'common:mediaHelper.video.play',
    playlistLabel: 'common:mediaType.videoPlaylist',
    pluralTitle: 'common:mediaType.video_other',
    prefixCountInParenthesis: 'common:mediaHelper.video.prefixCountInParenthesis',
    primary: 'common:profile.primaryVideo',
    rearrangeSuccess: 'common:mediaHelper.video.rearrangeSuccess',
    rearrangeTitle: 'common:mediaHelper.video.rearrangeTitle',
    requested: 'common:mediaHelper.video.requested',
    setAsPrimary: 'common:profile.button.setVideoReel',
    setAsPrimaryShort: 'common:profile.button.setVideoReel',
    suffixCountInParenthesis: 'common:mediaHelper.video.suffixCountInParenthesis',
    title: 'common:mediaType.video',
    upload: 'common:mediaHelper.video.upload',
    uploadSuccess: 'common:mediaHelper.video.uploadSuccess',
    removeAsPrimary: 'common:mediaHelper.video.removeAsPrimary',
    noMediaCurrently: 'common:mediaHelper.video.noMediaCurrentlyOnProfile',
  },
  photo: {
    add: 'common:mediaHelper.photo.add',
    addClip: 'common:mediaHelper.photo.addOne',
    addThis: 'common:mediaHelper.photo.addThis',
    confirmDelete: 'common:mediaHelper.photo.confirmDelete',
    count: 'common:mediaHelper.photo.count',
    delete: 'common:mediaHelper.photo.delete',
    deleteAll: 'common:mediaHelper.photo.deleteAll',
    deleteAllWarning: 'common:mediaHelper.photo.deleteAllWarning',
    deleteWarning: 'common:mediaHelper.photo.deleteWarning',
    edit: 'common:mediaHelper.photo.edit',
    editFailure: 'common:mediaHelper.photo.editFailure',
    editSuccess: 'common:mediaHelper.photo.editSuccess',
    noFiles: 'common:mediaHelper.photo.noFiles',
    noneOnProfile: 'common:mediaHelper.photo.noneShownOnProfile',
    play: 'common:mediaHelper.photo.play',
    pluralTitle: 'common:mediaType.photo_other',
    prefixCountInParenthesis: 'common:mediaHelper.photo.prefixCountInParenthesis',
    primary: 'common:profile.primaryPhoto',
    rearrangeSuccess: 'common:mediaHelper.photo.rearrangeSuccess',
    rearrangeTitle: 'common:mediaHelper.photo.rearrangeTitle',
    setAsPrimary: 'common:profile.button.setPrimaryPhoto',
    setAsPrimaryShort: 'common:profile.button.setPrimaryPhotoShort',
    requested: 'common:mediaHelper.photo.requested',
    suffixCountInParenthesis: 'common:mediaHelper.photo.suffixCountInParenthesis',
    title: 'common:mediaType.photo',
    upload: 'common:mediaHelper.photo.upload',
    uploadSuccess: 'common:mediaHelper.photo.uploadSuccess',
    removeAsPrimary: 'common:mediaHelper.photo.removeAsPrimary',
    noMediaCurrently: 'common:mediaHelper.photo.noMediaCurrentlyOnProfile',
    playlistLabel: '',
  },
  pdf: {
    add: 'common:mediaHelper.document.add',
    addClip: 'common:mediaHelper.document.addClip',
    addThis: 'common:mediaHelper.document.add',
    count: 'common:mediaHelper.pdf.count',
    deleteAll: 'common:mediaHelper.pdf.deleteAll',
    deleteAllWarning: 'common:mediaHelper.pdf.deleteAllWarning',
    edit: 'common:mediaHelper.document.edit',
    editFailure: 'common:mediaHelper.document.editFailure',
    editSuccess: 'common:mediaHelper.document.editSuccess',
    noFiles: 'common:mediaHelper.pdf.noFiles',
    play: 'common:mediaHelper.document.play',
    pluralTitle: 'common:mediaType.pdf_other',
    prefixCountInParenthesis: 'common:mediaHelper.pdf.prefixCountInParenthesis',
    rearrangeSuccess: 'common:mediaHelper.document.rearrangeSuccess',
    rearrangeTitle: 'common:mediaHelper.document.rearrangeTitle',
    requested: 'common:mediaHelper.document.requested',
    suffixCountInParenthesis: 'common:mediaHelper.pdf.suffixCountInParenthesis',
    title: 'common:mediaType.pdf',
    upload: 'common:mediaHelper.pdf.upload',
    uploadSuccess: 'common:mediaHelper.document.uploadSuccess',
    // Deleting PDFs is not allowed
    delete: '',
    deleteWarning: '',
    confirmDelete: '',
    noneOnProfile: '',
    setAsPrimary: '',
    // There is no "primary" PDF
    setAsPrimaryShort: '',
    primary: '',
    removeAsPrimary: '',
    noMediaCurrently: '',
    playlistLabel: '',
  },
};

export const useMediaText = (): MediaTextFn => {
  const { t } = useTranslation();

  return useCallback(
    (mediaType, type, opts = {}) => {
      const mediaTypeLowCase = mediaType?.toLowerCase();

      switch (mediaTypeLowCase) {
        case gqlRequestMediaType.AUDIO?.toLowerCase():
          return t(media.audio[type], opts);
        case gqlRequestMediaType.VIDEO?.toLowerCase():
          return t(media.video[type], opts);
        case gqlRequestMediaType.PHOTO?.toLowerCase():
          return t(media.photo[type], opts);
        case MEDIA_TYPE.DOCUMENT.toLowerCase():
          return t(media.document[type], opts);
        case gqlRequestMediaType.PDF?.toLowerCase():
          return t(media.pdf[type], opts);
        default:
          return '';
      }
    },
    [t]
  );
};

export const getMediaTypeExtensions = (mediaType: string): string[] | null => {
  if (mediaType === MEDIA_TYPE.PHOTO) {
    return appConfig.photoExtensions;
  }

  return null;
};

export const getMediaNameWithExtension = (media, maxLength?: number): string => {
  if (!media?.name) return '';

  let mediaNameBase = media?.name;
  const extension = media?.fileType?.code?.toLowerCase();

  if (media?.fileType?.code && !mediaNameBase?.endsWith(extension)) {
    mediaNameBase = `${mediaNameBase}.${extension}`;
  }

  return maxLength ? mediaNameBase?.substr(0, maxLength) : mediaNameBase;
};
/**
 * function for rearrangeMedias(or any list of objects) where
 *
 * list : array of objects
 * index: index of item for move
 * usIp : direction is ^Up^
 * skipAll : flag to indicate if should be first/last item
 */
export const rearrangeMedias = <T>(list: T[], index: number, isUp: boolean, skipAll: boolean): T[] => {
  let moveIndex;

  if (isUp) {
    moveIndex = skipAll ? 0 : index - 1;
  } else {
    moveIndex = skipAll ? list.length - 1 : index + 1;
  }

  const newList = list.slice();
  const media4move = newList.splice(index, 1)[0];

  newList.splice(moveIndex, 0, media4move);

  return newList;
};
